import { useMemo, useRef, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Stack,
  Divider,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Skeleton,
  IconButton,
  Tooltip,
  Autocomplete,
  Dialog,
} from '@mui/material'
import { useTheme } from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'
import BadgeIcon from '@mui/icons-material/Badge'
import EmailIcon from '@mui/icons-material/Email'
import ShieldIcon from '@mui/icons-material/Shield'
import LoginIcon from '@mui/icons-material/Login'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LanguageIcon from '@mui/icons-material/Language'
import ColorLensIcon from '@mui/icons-material/ColorLens'
import { useTranslation } from 'react-i18next'

import { useAccount, useUpdateAccount } from '../hooks/useAccount'
import { logout } from '../api/auth'

// ── Google Calendar event color options ───────────────────────────────────────

const CALENDAR_COLORS: { id: string; name: string; hex: string }[] = [
  { id: '1',  name: 'Lavender',  hex: '#7986CB' },
  { id: '2',  name: 'Sage',      hex: '#33B679' },
  { id: '3',  name: 'Grape',     hex: '#8E24AA' },
  { id: '4',  name: 'Flamingo',  hex: '#E67C73' },
  { id: '5',  name: 'Banana',    hex: '#F6BF26' },
  { id: '6',  name: 'Tangerine', hex: '#F4511E' },
  { id: '7',  name: 'Peacock',   hex: '#039BE5' },
  { id: '8',  name: 'Graphite',  hex: '#616161' },
  { id: '9',  name: 'Blueberry', hex: '#3F51B5' },
  { id: '10', name: 'Basil',     hex: '#0B8043' },
  { id: '11', name: 'Tomato',    hex: '#D50000' },
]

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const formatHour = (h: number) => `${String(h).padStart(2, '0')}:00`

const TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone').map((tz) => ({ id: tz, label: tz }))

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <Tooltip title={copied ? t('account.copied') : t('account.copy')} placement="right">
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{ color: copied ? 'success.main' : 'text.disabled', ml: 0.5, p: 0.5 }}
      >
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  )
}

// ── Detail row (icon + label + content) ───────────────────────────────────────

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          background: 'rgba(255,255,255,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: 'text.secondary',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Box sx={{ mt: 0.25 }}>{children}</Box>
      </Box>
    </Stack>
  )
}

// ── Card section heading (icon + title + optional right element) ───────────────

function CardHeading({
  icon,
  title,
  action,
}: {
  icon: React.ReactNode
  title: string
  action?: React.ReactNode
}) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 18 }}>{icon}</Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface SettingsForm {
  timezone: string
  icsCalendarUrl: string
  eventTitle: string
  eventColor: string   // kept as string for Select compatibility; converted to number on save
  workHoursStart: number
  workHoursEnd: number
}

export default function AccountPage() {

  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'

  const { data: user, isLoading } = useAccount({ fresh: true })
  const { mutate: save, isPending } = useUpdateAccount()
  const [loggingOut, setLoggingOut] = useState(false)
  const [avatarOpen, setAvatarOpen] = useState(false)

  // ── Calendar edit state ──
  const [editingCalendar, setEditingCalendar] = useState(false)
  const [calendarSaveError, setCalendarSaveError] = useState(false)

  // ── Timezone & Schedule edit state ──
  const [editingHours, setEditingHours] = useState(false)
  const [hoursSaveError, setHoursSaveError] = useState(false)

  const initialForm = useMemo<SettingsForm>(() => ({
    timezone: user?.timezone ?? '',
    icsCalendarUrl: user?.icsCalendarUrl ?? '',
    eventTitle: user?.eventTitle ?? '',
    eventColor: user?.eventColor != null ? String(user.eventColor) : '',
    workHoursStart: user?.workHoursStart ?? 9,
    workHoursEnd: user?.workHoursEnd ?? 18,
  }), [user])

  const [form, setForm] = useState<SettingsForm>(initialForm)
  const [baseline, setBaseline] = useState<SettingsForm>(initialForm)

  // Reset form when user data changes (e.g. after save or refetch)
  const userKey = user?.userId
  const prevUserKeyRef = useRef(userKey)
  if (userKey !== prevUserKeyRef.current) {
    prevUserKeyRef.current = userKey
    setForm(initialForm)
    setBaseline(initialForm)
  }

  const set = (field: keyof SettingsForm, value: unknown) =>
    setForm((f) => ({ ...f, [field]: value }))

  const calendarDirty = useMemo(() => {
    if (!baseline) return false
    return (
      form.icsCalendarUrl !== baseline.icsCalendarUrl ||
      form.eventTitle !== baseline.eventTitle ||
      form.eventColor !== baseline.eventColor
    )
  }, [form, baseline])

  const hoursDirty = useMemo(() => {
    if (!baseline) return false
    return (
      form.timezone !== baseline.timezone ||
      form.workHoursStart !== baseline.workHoursStart ||
      form.workHoursEnd !== baseline.workHoursEnd
    )
  }, [form, baseline])

  // Client-side mirror of the backend @ValidIcsUrl + @VictorOpsIcsUrl constraints: blank is allowed
  // (disables sync); otherwise must be an http(s) URL whose host is portal.victorops.com.
  const icsUrlError = useMemo<string | null>(() => {
    const value = form.icsCalendarUrl.trim()
    if (!value) return null
    let parsed: URL
    try {
      parsed = new URL(value)
    } catch {
      return t('account.icsUrlInvalid', { defaultValue: 'Enter a valid URL (https://…)' })
    }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return t('account.icsUrlInvalid', { defaultValue: 'Enter a valid URL (https://…)' })
    }
    if (parsed.hostname.toLowerCase() !== 'portal.victorops.com') {
      return t('account.icsUrlHost', { defaultValue: 'Must be a VictorOps URL (portal.victorops.com)' })
    }
    return null
  }, [form.icsCalendarUrl, t])

  const buildPayload = (overrides: Partial<SettingsForm> = {}) => {
    const colorStr = (overrides.eventColor ?? form.eventColor) || ''
    const icsUrl = (overrides.icsCalendarUrl ?? form.icsCalendarUrl).trim()
    const title = (overrides.eventTitle ?? form.eventTitle).trim()
    return {
      timezone: overrides.timezone ?? form.timezone,
      icsCalendarUrl: icsUrl || undefined,
      eventTitle: title || undefined,
      eventColor: colorStr ? parseInt(colorStr, 10) : undefined,
      workHoursStart: overrides.workHoursStart ?? form.workHoursStart,
      workHoursEnd: overrides.workHoursEnd ?? form.workHoursEnd,
    }
  }

  const handleSaveCalendar = () => {
    if (icsUrlError) return
    setCalendarSaveError(false)
    save(buildPayload(), {
      onSuccess: () => {
        setEditingCalendar(false)
        setBaseline((b) => b ? { ...b, icsCalendarUrl: form.icsCalendarUrl, eventTitle: form.eventTitle, eventColor: form.eventColor } : b)
      },
      onError: () => setCalendarSaveError(true),
    })
  }

  const handleSaveHours = () => {
    setHoursSaveError(false)
    save(buildPayload(), {
      onSuccess: () => {
        setEditingHours(false)
        setBaseline((b) => b ? { ...b, timezone: form.timezone, workHoursStart: form.workHoursStart, workHoursEnd: form.workHoursEnd } : b)
      },
      onError: () => setHoursSaveError(true),
    })
  }

  const cancelCalendarEdit = () => {
    if (baseline) setForm((f) => ({ ...f, icsCalendarUrl: baseline.icsCalendarUrl, eventTitle: baseline.eventTitle, eventColor: baseline.eventColor }))
    setEditingCalendar(false)
    setCalendarSaveError(false)
  }

  const cancelHoursEdit = () => {
    if (baseline) setForm((f) => ({ ...f, timezone: baseline.timezone, workHoursStart: baseline.workHoursStart, workHoursEnd: baseline.workHoursEnd }))
    setEditingHours(false)
    setHoursSaveError(false)
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    await logout()
  }

  const providerLabel =
    user?.provider === 'MICROSOFT'
      ? t('account.microsoft')
      : user?.provider === 'GOOGLE'
      ? t('account.google')
      : user?.provider ?? '—'

  const selectedColor = CALENDAR_COLORS.find((c) => c.id === form.eventColor)

  return (
    <Box>
      {/* ── Profile hero ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(66,133,244,0.10) 0%, rgba(66,133,244,0.03) 100%)',
          border: '1px solid rgba(66,133,244,0.18)',
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Avatar
          src={user?.avatarUrl}
          alt={user?.name}
          sx={{
            width: 88,
            height: 88,
            fontSize: '2.2rem',
            flexShrink: 0,
            cursor: user?.avatarUrl ? 'pointer' : 'default',
          }}
          slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
          onClick={() => user?.avatarUrl && setAvatarOpen(true)}
        >
          {user?.name ? user.name.charAt(0).toUpperCase() : <PersonIcon fontSize="large" />}
        </Avatar>

        {/* Avatar photo dialog */}
        <Dialog open={avatarOpen} onClose={() => setAvatarOpen(false)} maxWidth="sm">
          <img
            src={user?.avatarUrl ?? ''}
            alt={user?.name}
            referrerPolicy="no-referrer"
            style={{ display: 'block', maxWidth: '100%', maxHeight: '80vh' }}
          />
        </Dialog>

        <Box flex={1} minWidth={0}>
          <Typography sx={{ typography: { xs: 'h5', sm: 'h4' }, fontWeight: 700 }} noWrap>
            {user?.name ?? '—'}
          </Typography>
          <Typography variant="body1" color="text.secondary" noWrap>
            {user?.email ?? '—'}
          </Typography>
        </Box>

        <Tooltip title={loggingOut ? t('account.loggingOut') : t('account.logOut')}>
          <span>
            {/* Icon-only on mobile, full button on desktop */}
            <IconButton
              color="error"
              onClick={handleLogout}
              disabled={loggingOut}
              sx={{
                display: { xs: 'flex', sm: 'none' },
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: 1.5,
                flexShrink: 0,
              }}
            >
              <LogoutIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          disabled={loggingOut}
          sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
        >
          {loggingOut ? t('account.loggingOut') : t('account.logOut')}
        </Button>
      </Box>

      {/* ── 4-card grid ── */}
      <Grid container spacing={3}>

        {/* ── Account Details ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <CardHeading icon={<PersonIcon fontSize="inherit" />} title={t('account.accountDetails')} />
              <Divider sx={{ borderColor, mb: 2.5 }} />
              <Stack spacing={2.5}>
                <DetailRow icon={<BadgeIcon fontSize="small" />} label={t('account.fullName')}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="body1" fontWeight={500}>
                      {user?.name ?? '—'}
                    </Typography>
                    {user?.name && <CopyButton text={user.name} />}
                  </Stack>
                </DetailRow>

                <DetailRow icon={<EmailIcon fontSize="small" />} label={t('account.email')}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="body1" fontWeight={500}>
                      {user?.email ?? '—'}
                    </Typography>
                    {user?.email && <CopyButton text={user.email} />}
                  </Stack>
                </DetailRow>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Login & Security ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <CardHeading icon={<ShieldIcon fontSize="inherit" />} title={t('account.loginSecurity')} />
              <Divider sx={{ borderColor, mb: 2.5 }} />
              <Stack spacing={2.5}>
                <DetailRow icon={<LoginIcon fontSize="small" />} label={t('account.loginProvider')}>
                  <Typography variant="body1" fontWeight={500}>
                    {providerLabel}
                  </Typography>
                </DetailRow>

                <DetailRow icon={<ShieldIcon fontSize="small" />} label={t('account.role')}>
                  <Typography variant="body1" fontWeight={500}>
                    {user?.role === 'ADMIN' ? t('account.administrator') : t('account.user')}
                  </Typography>
                </DetailRow>

              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Calendar ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <CardHeading
                icon={<CalendarMonthIcon fontSize="inherit" />}
                title={t('account.calendar')}
                action={
                  !editingCalendar ? (
                    <Tooltip title={t('account.edit')}>
                      <IconButton size="small" onClick={() => setEditingCalendar(true)} sx={{ color: 'text.secondary' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : undefined
                }
              />
              <Divider sx={{ borderColor, mb: 2.5 }} />

              {user?.icsSyncStatus === 'NEEDS_ATTENTION' && (
                <Alert severity="warning" sx={{ mb: 2.5 }}>
                  {t('account.icsNeedsAttention', {
                    defaultValue: 'ICS calendar sync is paused — the link looks invalid or expired. '
                      + 'Update the VictorOps ICS URL and save to resume.',
                  })}
                </Alert>
              )}

              {isLoading ? (
                <Stack spacing={2}>
                  <Skeleton variant="rounded" height={48} />
                  <Skeleton variant="rounded" height={48} />
                  <Skeleton variant="rounded" height={48} />
                </Stack>
              ) : !editingCalendar ? (
                // ── Read-only view ──
                <Stack spacing={2.5}>
                  <DetailRow icon={<CalendarMonthIcon fontSize="small" />} label={t('account.icsUrl', { defaultValue: 'VictorOps ICS URL' })}>
                    {form.icsCalendarUrl ? (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 220 }} title={form.icsCalendarUrl}>
                          {form.icsCalendarUrl}
                        </Typography>
                        <CopyButton text={form.icsCalendarUrl} />
                      </Stack>
                    ) : (
                      <Typography variant="body1" fontWeight={500} color="text.secondary">
                        {t('account.icsNotConfigured', { defaultValue: 'Not configured' })}
                      </Typography>
                    )}
                  </DetailRow>

                  <DetailRow icon={<BadgeIcon fontSize="small" />} label={t('account.eventTitle', { defaultValue: 'Event title' })}>
                    <Typography variant="body1" fontWeight={500} color={form.eventTitle ? 'text.primary' : 'text.secondary'}>
                      {form.eventTitle || t('account.eventTitleDefault', { defaultValue: 'VictorOps summary' })}
                    </Typography>
                  </DetailRow>

                  <DetailRow icon={<ColorLensIcon fontSize="small" />} label={t('account.eventColorLabel', { defaultValue: 'On-call event color' })}>
                    {selectedColor ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: selectedColor.hex, flexShrink: 0, border: '1px solid rgba(255,255,255,0.15)' }} />
                        <Typography variant="body1" fontWeight={500}>{t('account.colors.' + selectedColor.name, { defaultValue: selectedColor.name })}</Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body1" fontWeight={500} color="text.secondary">
                        {t('account.noFilter')}
                      </Typography>
                    )}
                  </DetailRow>
                </Stack>
              ) : (
                // ── Edit view ──
                <Stack spacing={3}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                    <TextField
                      fullWidth
                      size="small"
                      sx={{ flex: 2 }}
                      label={t('account.icsUrl', { defaultValue: 'VictorOps ICS URL' })}
                      placeholder="https://portal.victorops.com/api/v1/user/calendar/…ics"
                      value={form.icsCalendarUrl}
                      onChange={(e) => set('icsCalendarUrl', e.target.value)}
                      error={!!icsUrlError}
                      helperText={icsUrlError ?? t('account.icsUrlHelp', {
                        defaultValue: 'Synced into your primary Google Calendar every 15 minutes',
                      })}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      sx={{ flex: 1 }}
                      label={t('account.eventTitle', { defaultValue: 'Event title' })}
                      placeholder={t('account.eventTitlePlaceholder', { defaultValue: 'e.g. your name' })}
                      value={form.eventTitle}
                      onChange={(e) => set('eventTitle', e.target.value)}
                      helperText={t('account.eventTitleHelp', {
                        defaultValue: 'Custom title for synced events (max 30); blank = VictorOps summary',
                      })}
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 30 } }}
                    />
                  </Stack>

                  <FormControl fullWidth size="small">
                    <InputLabel id="cal-color-label">{t('account.eventColorLabel', { defaultValue: 'On-call event color' })}</InputLabel>
                    <Select
                      labelId="cal-color-label"
                      value={form.eventColor}
                      label={t('account.eventColorLabel', { defaultValue: 'On-call event color' })}
                      onChange={(e) => set('eventColor', e.target.value)}
                      renderValue={(value) => {
                        if (!value) return <em style={{ color: 'rgba(255,255,255,0.5)' }}>{t('account.noFilter')}</em>
                        const color = CALENDAR_COLORS.find((c) => c.id === value)
                        return color ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color.hex, flexShrink: 0, border: '1px solid rgba(255,255,255,0.15)' }} />
                            <span>{t('account.colors.' + color.name, { defaultValue: color.name })}</span>
                          </Stack>
                        ) : value
                      }}
                    >
                      <MenuItem value=""><em>{t('account.noFilter')}</em></MenuItem>
                      {CALENDAR_COLORS.map((color) => (
                        <MenuItem key={color.id} value={color.id}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color.hex, flexShrink: 0, border: '1px solid rgba(255,255,255,0.15)' }} />
                            <span>{t('account.colors.' + color.name, { defaultValue: color.name })}</span>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{t('account.colorHelp')}</FormHelperText>
                  </FormControl>

                  {calendarSaveError && (
                    <Alert severity="error" sx={{ py: 0 }}>{t('account.saveFailed')}</Alert>
                  )}

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveCalendar}
                      disabled={isPending || !calendarDirty || !!icsUrlError}
                      sx={{ minWidth: 80 }}
                    >
                      {isPending ? t('account.saving') : t('account.save')}
                    </Button>
                    <Button size="small" color="inherit" onClick={cancelCalendarEdit} disabled={isPending}>
                      {t('account.cancel')}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Timezone & Schedule ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <CardHeading
                icon={<AccessTimeIcon fontSize="inherit" />}
                title={t('account.timezoneSchedule')}
                action={
                  !editingHours ? (
                    <Tooltip title={t('account.edit')}>
                      <IconButton size="small" onClick={() => setEditingHours(true)} sx={{ color: 'text.secondary' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : undefined
                }
              />
              <Divider sx={{ borderColor, mb: 2.5 }} />

              {isLoading ? (
                <Stack spacing={2}>
                  <Skeleton variant="rounded" height={48} />
                  <Skeleton variant="rounded" height={48} />
                </Stack>
              ) : !editingHours ? (
                // ── Read-only view ──
                <Stack spacing={2.5}>
                  <DetailRow icon={<LanguageIcon fontSize="small" />} label={t('account.timezone')}>
                    <Typography variant="body1" fontWeight={500}>
                      {form.timezone || '—'}
                    </Typography>
                  </DetailRow>

                  <DetailRow icon={<AccessTimeIcon fontSize="small" />} label={t('account.officeHours')}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body1" fontWeight={500}>{formatHour(form.workHoursStart)}</Typography>
                      <Typography variant="body2" color="text.secondary">–</Typography>
                      <Typography variant="body1" fontWeight={500}>{formatHour(form.workHoursEnd)}</Typography>
                    </Stack>
                  </DetailRow>
                </Stack>
              ) : (
                // ── Edit view ──
                <Stack spacing={3}>
                  <Autocomplete
                    options={TIMEZONE_OPTIONS}
                    value={TIMEZONE_OPTIONS.find((o) => o.id === form.timezone) ?? null}
                    onChange={(_, v) => { if (v) set('timezone', v.id) }}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    size="small"
                    slotProps={{ paper: { elevation: 8 } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('account.timezone')}
                        placeholder={t('account.timezonePlaceholder')}
                        helperText={t('account.timezoneHelp')}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                  />

                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <FormControl fullWidth size="small">
                      <InputLabel id="work-start-label">{t('account.starts')}</InputLabel>
                      <Select
                        labelId="work-start-label"
                        value={form.workHoursStart}
                        label={t('account.starts')}
                        onChange={(e) => set('workHoursStart', Number(e.target.value))}
                      >
                        {HOURS.filter((h) => h < form.workHoursEnd).map((h) => (
                          <MenuItem key={h} value={h}>{formatHour(h)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1.1, flexShrink: 0 }}>–</Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel id="work-end-label">{t('account.ends')}</InputLabel>
                      <Select
                        labelId="work-end-label"
                        value={form.workHoursEnd}
                        label={t('account.ends')}
                        onChange={(e) => set('workHoursEnd', Number(e.target.value))}
                      >
                        {HOURS.filter((h) => h > form.workHoursStart).map((h) => (
                          <MenuItem key={h} value={h}>{formatHour(h)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  {hoursSaveError && (
                    <Alert severity="error" sx={{ py: 0 }}>{t('account.saveFailed')}</Alert>
                  )}

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveHours}
                      disabled={isPending || !hoursDirty}
                      sx={{ minWidth: 80 }}
                    >
                      {isPending ? t('account.saving') : t('account.save')}
                    </Button>
                    <Button size="small" color="inherit" onClick={cancelHoursEdit} disabled={isPending}>
                      {t('account.cancel')}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  )
}
